import { Icon } from '@iconify/react';
import debounce from 'lodash/debounce';
import { FC, useEffect, useMemo, useState } from 'react';

import { Input, InputProps } from 'components/data-entry/Input';

import { cn } from 'utils/cn';

interface SearchProps extends Omit<InputProps, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  clearDataCy?: string;
  'data-cy'?: string;
  placeholder?: string;
  fullWidth?: boolean;
  /** Debounce delay in milliseconds. When set, onChange is debounced.
   * Set to `0` or `undefined` to disable debouncing.
   * @default 200
   */
  debounceMs?: number;
}

export const Search: FC<SearchProps> = ({
  value,
  onChange,
  clearDataCy,
  'data-cy': dataCy,
  placeholder,
  fullWidth = false,
  debounceMs = 200,
  ...props
}) => {
  // Internal state for immediate input display when debouncing
  const [internalValue, setInternalValue] = useState(value);

  // Sync internal state when external value changes (e.g., cleared externally)
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Create debounced onChange callback
  const debouncedOnChange = useMemo(() => {
    if (!debounceMs) return null;
    return debounce((newValue: string) => onChange(newValue), debounceMs);
  }, [onChange, debounceMs]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedOnChange?.cancel();
    };
  }, [debouncedOnChange]);

  const handleChange = (newValue: string) => {
    if (debounceMs && debouncedOnChange) {
      setInternalValue(newValue);
      debouncedOnChange(newValue);
    } else {
      onChange(newValue);
    }
  };

  const handleClear = () => {
    setInternalValue('');
    debouncedOnChange?.cancel();
    onChange('');
  };

  const displayValue = debounceMs ? internalValue : value;

  return (
    <Input
      placeholder={placeholder || 'Search'}
      value={displayValue}
      onChange={(e) => handleChange(e.target.value)}
      data-cy={dataCy}
      prefix={<Icon icon="ant-design:search-outlined" />}
      suffix={
        displayValue && (
          <Icon
            icon="ant-design:close-outlined"
            className="cursor-pointer"
            data-cy={clearDataCy}
            onClick={handleClear}
          />
        )
      }
      className={cn('w-[200px]', fullWidth && 'w-full', props.className)}
      {...props}
    />
  );
};
